import React, { forwardRef, useCallback } from 'react'

import Box, { type BoxProps } from '../box'

/* -----------------------------------------------------------------------------
 * Pressable
 *
 * A Box that responds to clicks, and to Enter/Space while focused, like
 * react-native's Pressable on the web: a tab stop unless disabled, with a
 * pointer cursor while enabled.
 * -------------------------------------------------------------------------- */

export type PressableProps = BoxProps & {
  onPress?: (event: React.SyntheticEvent) => void
  onPressIn?: (event: React.SyntheticEvent) => void
  onPressOut?: (event: React.SyntheticEvent) => void
  disabled?: boolean
}

// react-native-web's Pressable root style, applied while enabled
const PRESSABLE_ROOT = { cursor: 'pointer', touchAction: 'manipulation' }

const Pressable = forwardRef<HTMLElement, PressableProps>(
  ({ onPress, onPressIn, onPressOut, disabled, focusable, ...props }, ref) => {
    const { onKeyDown } = props
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        onKeyDown?.(event)
        if (disabled || !onPress) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onPress(event)
        }
      },
      [disabled, onPress, onKeyDown]
    )

    return (
      <Box
        ref={ref}
        // @walrus/common's Pressable default; a caller's `style` replaces it
        style={{ cursor: 'pointer' }}
        {...props}
        kitLayers={{ classes: disabled ? {} : PRESSABLE_ROOT }}
        focusable={!disabled && focusable !== false}
        accessibilityState={
          disabled ? { disabled: true } : props.accessibilityState
        }
        onClick={disabled ? undefined : onPress}
        onMouseDown={disabled ? undefined : onPressIn}
        onMouseUp={disabled ? undefined : onPressOut}
        onKeyDown={handleKeyDown}
      />
    )
  }
)
Pressable.displayName = 'Pressable'

export default Pressable
